import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, Eye, EyeOff, Lock } from 'lucide-react';
import AppLogo from './AppLogo';
import { Button } from './ui/button';
import { verifyRecoverToken, updatePassword, signOut } from '../lib/session';

// Verify-on-click password-reset interstitial.
//   Trigger URL: `?recover=1#th=<token_hash>&em=<email>`
// The recovery token_hash sits in the URL FRAGMENT (never sent to a server,
// never logged) and is only exchanged for a short-lived session once a real
// human taps the CTA. This stops mail-security scanners that prefetch links
// from silently burning a one-tap password reset — a prefetch never runs our
// onClick handler, so it never calls GoTrue /verify.
//
// Once the reset succeeds, Supabase server-side invalidates every other
// refresh token on the account. To keep the UX clean we then sign the user
// OUT locally and drop them at the front door so they log in with the new
// password — that also proves to themselves that the new password works.
function parseRecoverTrigger() {
  try {
    const search = new URLSearchParams(window.location.search);
    if (search.get('recover') !== '1') return null;
    const rawHash = (window.location.hash || '').replace(/^#/, '');
    if (!rawHash) return null;
    const params = new URLSearchParams(rawHash);

    const error = params.get('error') || params.get('error_description');
    if (error) return { mode: 'error' };

    const th = params.get('th');
    if (!th) return null;
    const em = params.get('em') || '';
    return { mode: 'verify', th, em };
  } catch {
    return null;
  }
}

// Strip the trigger + fragment so a refresh or a copy-pasted URL never
// re-triggers the gate or re-exposes the (now-spent) token_hash.
function cleanRecoverUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('recover');
    url.hash = '';
    window.history.replaceState(window.history.state, '', url.toString());
  } catch { /* no-op */ }
}

function passwordProblem(pw, confirm) {
  if (!pw || pw.length < 6) return 'Choose a password with at least 6 characters.';
  if (pw !== confirm) return "Passwords don't match yet.";
  return null;
}

/**
 * Verify-on-click password-reset gate. Mounted globally next to ContinueGate.
 * Renders nothing when there's no `?recover=1#th=…` trigger in the URL.
 *
 * Flow:
 *   ready      — landing screen with "Reset my password"
 *   verifying  — exchanging the token_hash (transient)
 *   choose     — verified; show new-password + confirm-password fields
 *   saving     — PUT /user in flight (transient)
 *   done       — success confirmation; auto-signs-out then shows "Log in" CTA
 *   error      — link expired / already used / invalid
 */
export default function RecoverGate() {
  const [trigger, setTrigger] = useState(null);
  const [stage, setStage] = useState('ready');
  const [pw, setPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = parseRecoverTrigger();
    if (!t) return;
    setTrigger(t);
    if (t.mode === 'error') setStage('error');
  }, []);

  if (!trigger) return null;

  const startVerify = async () => {
    if (stage !== 'ready') return;
    setStage('verifying');
    setError(null);
    try {
      const ok = await verifyRecoverToken(trigger.th);
      if (!ok) { setStage('error'); return; }
      // Recovery session is now live in localStorage; move to password-set step.
      setStage('choose');
    } catch {
      setStage('error');
    }
  };

  const savePassword = async () => {
    if (stage === 'saving') return;
    const problem = passwordProblem(pw, confirmPw);
    if (problem) { setError(problem); return; }
    setError(null);
    setStage('saving');
    try {
      const { ok, error: apiError } = await updatePassword(pw);
      if (!ok) {
        setError(apiError || 'Could not update your password. Try requesting a new link.');
        setStage('choose');
        return;
      }
      // Wipe the recovery session locally so the user has to re-authenticate
      // with the NEW password — that's the E2E proof it worked. The server
      // has already invalidated every other refresh token as a side-effect of
      // updateUser({password}), so any stale session for this account is
      // already dead.
      signOut();
      cleanRecoverUrl();
      setPw(''); setConfirmPw('');
      setStage('done');
    } catch {
      setError('Network error — please try again.');
      setStage('choose');
    }
  };

  const finish = () => {
    setTrigger(null);
    // Full reload so App.js re-evaluates the wall / login state cleanly.
    try { window.location.assign(window.location.pathname); } catch { /* no-op */ }
  };

  const isError = stage === 'error';
  const isDone = stage === 'done';

  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[75] flex items-center justify-center p-4"
      role="dialog" aria-modal="true"
      aria-label="Reset password"
      data-testid="recover-gate"
    >
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-xl overflow-hidden">
        <div className="p-6 text-center">
          <AppLogo size="lg" className="mx-auto mb-3" />
          <h1 className="text-xl font-extrabold text-[#0F172A]">IhsanOne</h1>

          {stage === 'ready' && (
            <>
              <p className="text-sm text-muted-foreground mt-1.5">Reset your password</p>
              {trigger.em && (
                <p className="text-sm mt-4 text-[#0F172A]">
                  Resetting for <span className="font-semibold">{trigger.em}</span>
                </p>
              )}
              <Button
                type="button"
                data-testid="recover-gate-button"
                onClick={startVerify}
                className="w-full h-12 text-base mt-5"
                size="lg"
              >
                Reset my password
              </Button>
            </>
          )}

          {stage === 'verifying' && (
            <p className="text-sm text-muted-foreground mt-4">Checking your link…</p>
          )}

          {(stage === 'choose' || stage === 'saving') && (
            <div className="mt-4 text-left" data-testid="recover-gate-password-form">
              <p className="text-sm text-[#0F172A] text-center">
                {trigger.em ? (
                  <>Set a new password for <span className="font-semibold">{trigger.em}</span></>
                ) : (
                  'Set a new password'
                )}
              </p>

              <div className="mt-4">
                <label htmlFor="recover-pw" className="block text-xs font-semibold text-[#0F172A] mb-1.5">
                  New password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    id="recover-pw"
                    data-testid="recover-gate-new-password"
                    type={showPw ? 'text' : 'password'}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                    className="w-full h-11 rounded-xl bg-muted/60 pl-9 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center rounded-lg text-muted-foreground"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="mt-3">
                <label htmlFor="recover-pw-confirm" className="block text-xs font-semibold text-[#0F172A] mb-1.5">
                  Confirm new password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    id="recover-pw-confirm"
                    data-testid="recover-gate-confirm-password"
                    type={showConfirmPw ? 'text' : 'password'}
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    className="w-full h-11 rounded-xl bg-muted/60 pl-9 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPw((s) => !s)}
                    aria-label={showConfirmPw ? 'Hide password' : 'Show password'}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center rounded-lg text-muted-foreground"
                  >
                    {showConfirmPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <p role="alert" data-testid="recover-gate-error-message" className="mt-3 text-xs font-semibold text-rose-600">
                  {error}
                </p>
              )}

              <Button
                type="button"
                data-testid="recover-gate-save-button"
                onClick={savePassword}
                disabled={stage === 'saving'}
                className="w-full h-12 text-base mt-5"
                size="lg"
              >
                {stage === 'saving' ? 'Saving…' : 'Save new password'}
              </Button>
            </div>
          )}

          {isDone && (
            <div className="mt-4" data-testid="recover-gate-success">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="h-7 w-7 text-primary" />
              </div>
              <p className="text-sm font-semibold text-[#0F172A]">
                Password updated.
              </p>
              <p className="text-sm text-muted-foreground mt-1.5">
                Log in with your new password to continue.
              </p>
              <Button
                type="button"
                data-testid="recover-gate-continue-button"
                onClick={finish}
                className="w-full h-12 text-base mt-5"
                size="lg"
              >
                Go to log in
              </Button>
            </div>
          )}

          {isError && (
            <div className="mt-4" data-testid="recover-gate-error">
              <div className="w-14 h-14 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertTriangle className="h-7 w-7 text-destructive" />
              </div>
              <p className="text-sm font-semibold text-[#0F172A]">
                This reset link has expired or was already used.
              </p>
              <p className="text-sm text-muted-foreground mt-1.5">
                Request a new password reset from the log-in screen.
              </p>
              <Button
                type="button"
                data-testid="recover-gate-error-close"
                onClick={finish}
                className="w-full h-12 text-base mt-5"
                size="lg"
              >
                Back to log in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
